import { Link } from "react-router-dom";
import { ArrowRight, Check, Wand2, BarChart3, Users, Palette, Lock, Zap, Type, Image as ImageIcon, Send, QrCode, Layers, Download } from "lucide-react";
import { MarketingLayout, PricingBlock } from "./MarketingHome";
import { SectionShell, EditorialHeading, Eyebrow, Lede, Button, Pill, Card } from "../design/Marketing";
import { api } from "../../lib/api";
import { useEffect, useState } from "react";

const F = [
  { icon: Wand2, t: "AI-assisted setup", d: "Describe your page in a sentence and Linkit drafts a starting point — themes, blocks, and copy. You edit anything you don't like." },
  { icon: Palette, t: "Full brand customization", d: "Themes, brand colours, fonts, backgrounds, button styles. Your page should feel like you — not a template." },
  { icon: BarChart3, t: "Real analytics", d: "Page views, unique visitors, clicks, click-through rate, top links, daily activity charts, CSV export." },
  { icon: Users, t: "Lead capture inbox", d: "Add a form to your page and collect names, emails, phones. Get an email each time — never miss a lead." },
  { icon: Layers, t: "Collapsible sections", d: "Group your links into premium collapsible tiles — perfect for creators with many recommendations." },
  { icon: QrCode, t: "Share block + QR code", d: "One block that lets visitors copy, share, or scan your page URL — great for events and print media." },
  { icon: Type, t: "Text, video, gallery blocks", d: "Long-form recommendations, embedded YouTube/Spotify, image galleries — not just a list of buttons." },
  { icon: ImageIcon, t: "Universal image cropper", d: "Every image (avatar, product, banner) is croppable and re-croppable without re-uploading." },
  { icon: Zap, t: "Drag-and-drop editor", d: "Reorder blocks with a drag. Hide with an eye toggle. Delete with a click. Zero learning curve." },
  { icon: Send, t: "Voice-to-text prompts", d: "Speak your changes to the AI Builder — hands-free editing on mobile." },
  { icon: Lock, t: "Draft mode", d: "Every change is a draft. Publish when you're ready. Discard if you change your mind." },
  { icon: Download, t: "Export your data", d: "Take your leads and analytics with you as CSV anytime. Your audience belongs to you." },
];

export default function FeaturesPage() {
  return (
    <MarketingLayout>
      <SectionShell size="lg">
        <div className="max-w-3xl">
          <Pill className="mb-6">All features</Pill>
          <EditorialHeading as="h1" size="xxl">Everything included, no add-ons.</EditorialHeading>
          <Lede className="mt-6">Linkit ships as a single Pro plan. Every feature below is available from day one of your free trial.</Lede>
          <Button to="/signup" size="lg" variant="primary" className="mt-8">Start 30 days free <ArrowRight size={16} /></Button>
        </div>
      </SectionShell>
      <SectionShell size="md">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          {F.map(({ icon: Icon, t, d }) => (
            <Card key={t} className="p-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-[10px]" style={{ background: "var(--lm-terra-light)", color: "var(--lm-terra-dark)" }}>
                <Icon size={18} />
              </span>
              <p className="mt-4 text-[17px] font-semibold leading-snug" style={{ color: "var(--lm-ink)" }}>{t}</p>
              <p className="mt-2 text-[14px] leading-relaxed" style={{ color: "var(--lm-ink-3)" }}>{d}</p>
            </Card>
          ))}
        </div>
      </SectionShell>
      <PricingBlock />
    </MarketingLayout>
  );
}

export function AnalyticsPage() {
  const days = [12, 9, 18, 22, 16, 28, 34, 30, 26, 38, 45, 40, 52, 48, 51];
  const max = Math.max(...days);
  return (
    <MarketingLayout>
      <SectionShell size="lg">
        <div className="max-w-3xl">
          <Pill className="mb-6">Analytics</Pill>
          <EditorialHeading as="h1" size="xxl">Know what people actually care about.</EditorialHeading>
          <Lede className="mt-6">
            See which recommendations, products and partnerships get attention — without guessing. Every click, view and lead is logged and visualised.
          </Lede>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {[
            { label: "Views (last 30 days)", v: "4,281" },
            { label: "Unique visitors", v: "2,914" },
            { label: "Click-through rate", v: "6.4%" },
          ].map((s) => (
            <Card key={s.label} className="p-6">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--lm-ink-4)" }}>{s.label}</p>
              <p className="mt-3 text-[42px] font-bold leading-none" style={{ fontFamily: "var(--lm-display)", color: "var(--lm-ink)" }}>{s.v}</p>
            </Card>
          ))}
        </div>
        <Card className="mt-6 p-6">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--lm-ink-4)" }}>Daily activity</p>
            <Pill tone="ink">Last 15 days</Pill>
          </div>
          <div className="mt-6 flex h-32 items-end gap-1.5">
            {days.map((v, i) => (
              <div key={i} className="flex-1 rounded-t-[3px]" style={{ height: `${(v / max) * 100}%`, background: "var(--lm-ink)" }} />
            ))}
          </div>
        </Card>
      </SectionShell>
      <SectionShell size="md" className="bg-[color:var(--lm-cream-2)]/60">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
          {[
            "Views & unique visitors",
            "Link-level clicks & CTR",
            "Top links (bar chart)",
            "Compare vs prior period",
            "Daily activity line chart",
            "CSV export any time",
          ].map((t) => (
            <div key={t} className="flex items-start gap-3">
              <Check size={16} className="mt-1 shrink-0" style={{ color: "var(--lm-terra)" }} />
              <p className="text-[15px]" style={{ color: "var(--lm-ink-2)" }}>{t}</p>
            </div>
          ))}
        </div>
      </SectionShell>
      <PricingBlock />
    </MarketingLayout>
  );
}

const EXAMPLES = [
  { name: "Content creator", who: "YouTube & newsletter creators", color: "#3B2E23", desc: "Videos, newsletter signup, best-of highlights, lead capture — with links tracked so you know what's working." },
  { name: "Travel creator", who: "Photographers, vloggers", color: "#B84A26", desc: "Trip archive, hotel picks, gear list, camera store affiliate links — all in a mood-matched theme." },
  { name: "Coach / consultant", who: "1:1 mentors, therapists", color: "#1F3D2E", desc: "Book-a-call CTA, testimonials, courses, resources, and a lead form for enquiries." },
  { name: "Small business", who: "Cafés, boutiques, salons", color: "#1D3554", desc: "Menu, opening hours, contact form, reviews, order-online button, socials." },
  { name: "Affiliate creator", who: "Product-recommendation creators", color: "#3A1A45", desc: "Product carousels, discount codes, category collections, tracked outbound links." },
  { name: "Freelancer", who: "Designers, developers, writers", color: "#5C3510", desc: "Portfolio gallery, rate card, testimonial wall, hire-me contact form." },
];

export function ExamplesPage() {
  return (
    <MarketingLayout>
      <SectionShell size="lg">
        <div className="max-w-3xl">
          <Pill className="mb-6">Examples</Pill>
          <EditorialHeading as="h1" size="xxl">A page for every kind of creator.</EditorialHeading>
          <Lede className="mt-6">These are demo pages we've designed to show what's possible. Every layout, block, and colour is fully editable in your own Linkit.</Lede>
        </div>
      </SectionShell>
      <SectionShell size="md">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {EXAMPLES.map((ex) => (
            <Card key={ex.name} className="overflow-hidden">
              <div className="grid grid-cols-[1fr_140px]">
                <div className="p-6 sm:p-8">
                  <Pill tone="ink">Demo · {ex.who}</Pill>
                  <p className="mt-4 text-[22px] font-semibold" style={{ fontFamily: "var(--lm-display)", color: "var(--lm-ink)" }}>{ex.name}</p>
                  <p className="mt-2 text-[14px] leading-relaxed" style={{ color: "var(--lm-ink-3)" }}>{ex.desc}</p>
                </div>
                <div style={{ background: ex.color }}>
                  <div className="flex h-full flex-col items-center justify-center gap-2 p-4">
                    <div className="h-10 w-10 rounded-full" style={{ background: "rgba(255,255,255,0.16)" }} />
                    <div className="h-2 w-16 rounded-full" style={{ background: "rgba(255,255,255,0.34)" }} />
                    <div className="mt-2 w-full space-y-1">
                      {[1, 2, 3].map((i) => <div key={i} className="rounded-full py-1.5" style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.14)" }} />)}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </SectionShell>
      <PricingBlock />
    </MarketingLayout>
  );
}

export function PricingPage() {
  return (
    <MarketingLayout>
      <SectionShell size="lg">
        <div className="mx-auto max-w-3xl text-center">
          <Pill className="mb-6">Pricing</Pill>
          <EditorialHeading as="h1" size="xxl">Simple. Fair. Everything included.</EditorialHeading>
          <Lede className="mx-auto mt-6 max-w-2xl">No feature tiers. No usage limits. 30 days free — no card required.</Lede>
        </div>
      </SectionShell>
      <PricingBlock />
    </MarketingLayout>
  );
}

export function AboutPage() {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/linkit/branding").then((r) => setB(r.data)).catch(() => setB({})); }, []);
  const bio = b?.founder_bio || "";
  return (
    <MarketingLayout>
      <SectionShell size="lg">
        <div className="max-w-3xl">
          <Pill className="mb-6">About</Pill>
          <EditorialHeading as="h1" size="xxl">Built by a creator, for creators.</EditorialHeading>
          <Lede className="mt-6">
            Linkit isn't a link-in-bio built by a growth-hacking startup — it's built by a working creator who needed one and couldn't find it. Every feature comes from a real problem, not a roadmap meeting.
          </Lede>
        </div>
      </SectionShell>
      <SectionShell size="md" className="bg-[color:var(--lm-cream-2)]/60">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[280px_1fr] md:gap-16">
          <div>
            <div className="aspect-[4/5] w-full overflow-hidden rounded-[var(--lm-r-lg)] border" style={{ borderColor: "var(--lm-hair)", background: "var(--lm-cream)" }}>
              {b?.founder_avatar_url ? (
                <img src={b.founder_avatar_url} alt={b.founder_name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center" style={{ fontFamily: "var(--lm-display)", color: "var(--lm-ink-3)", fontSize: 96 }}>
                  {(b?.founder_name || "?").charAt(0)}
                </div>
              )}
            </div>
          </div>
          <div>
            <Eyebrow>The founder</Eyebrow>
            <EditorialHeading size="xl">I needed a better link in bio. So I built one.</EditorialHeading>
            <Lede className="mt-5">"{bio}"</Lede>
            <p className="mt-6 text-[15px] font-semibold" style={{ color: "var(--lm-ink-2)" }}>— {b?.founder_name}, {b?.founder_handle}</p>
            {b?.founder_social_url && (
              <a href={b.founder_social_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-[14px] font-semibold underline-offset-4 hover:underline" style={{ color: "var(--lm-terra)" }}>
                Follow @{b.founder_handle} <ArrowRight size={14} />
              </a>
            )}
          </div>
        </div>
      </SectionShell>
      <PricingBlock />
    </MarketingLayout>
  );
}

// ------------------------------------------------------------------ LEGAL PAGES
function LegalShell({ title, updated, children }) {
  return (
    <MarketingLayout>
      <SectionShell size="lg">
        <div className="max-w-3xl">
          <Pill className="mb-6">{title}</Pill>
          <EditorialHeading as="h1" size="xl">{title}</EditorialHeading>
          <p className="mt-3 text-[13px]" style={{ color: "var(--lm-ink-4)" }}>Last updated: {updated}</p>
          <div className="prose prose-neutral mt-8 max-w-none text-[15px] leading-[1.75]" style={{ color: "var(--lm-ink-2)" }}>
            {children}
          </div>
        </div>
      </SectionShell>
    </MarketingLayout>
  );
}
export function TermsPage() {
  return (
    <LegalShell title="Terms of Service" updated="February 2026">
      <h3 className="mt-6 text-[18px] font-semibold" style={{ color: "var(--lm-ink)" }}>1. Acceptance</h3>
      <p>By creating a Linkit account, you agree to these terms. If you're using Linkit for a company, you're agreeing on behalf of that company.</p>
      <h3 className="mt-6 text-[18px] font-semibold" style={{ color: "var(--lm-ink)" }}>2. Your account</h3>
      <p>You're responsible for the accuracy of your account info, keeping your password safe, and everything that happens under your account. Don't impersonate anyone, and don't use a Linkit username that infringes trademarks or a real person's identity.</p>
      <h3 className="mt-6 text-[18px] font-semibold" style={{ color: "var(--lm-ink)" }}>3. Trial and billing</h3>
      <p>Every new account gets 30 days free with no payment method required. At day 23 you may opt into a 15-day extension by authorising a Razorpay recurring payment method. Your first charge occurs after the full trial. You can cancel any time before your first billing date at no cost.</p>
      <h3 className="mt-6 text-[18px] font-semibold" style={{ color: "var(--lm-ink)" }}>4. Content you post</h3>
      <p>You own everything you upload. You grant Linkit a limited licence to host, display and serve your content solely to run your Linkit page. Don't upload anything unlawful, hateful, or that infringes on someone else's rights.</p>
      <h3 className="mt-6 text-[18px] font-semibold" style={{ color: "var(--lm-ink)" }}>5. Termination</h3>
      <p>You can close your account any time from Settings. We can suspend or terminate accounts that violate these terms. Your data will be retained per our privacy policy.</p>
      <h3 className="mt-6 text-[18px] font-semibold" style={{ color: "var(--lm-ink)" }}>6. Contact</h3>
      <p>Questions? Email hello@linkit.app.</p>
    </LegalShell>
  );
}
export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy" updated="February 2026">
      <p>We treat your data like we'd want ours treated. Here's a plain-English summary.</p>
      <h3 className="mt-6 text-[18px] font-semibold" style={{ color: "var(--lm-ink)" }}>What we collect</h3>
      <ul className="ml-5 list-disc space-y-2">
        <li>Your account details (name, email, username, country, category).</li>
        <li>The content of your Linkit page.</li>
        <li>Anonymous analytics events (page views, clicks) for your dashboard.</li>
        <li>Payment method identifiers stored by Razorpay — we never see full card numbers.</li>
      </ul>
      <h3 className="mt-6 text-[18px] font-semibold" style={{ color: "var(--lm-ink)" }}>How we use it</h3>
      <p>To operate your account, provide analytics, send you service emails (welcome, trial reminders, payment receipts), and improve the product. We do not sell your data.</p>
      <h3 className="mt-6 text-[18px] font-semibold" style={{ color: "var(--lm-ink)" }}>Your rights</h3>
      <p>You can access, export or delete your data any time. Email hello@linkit.app for help.</p>
      <h3 className="mt-6 text-[18px] font-semibold" style={{ color: "var(--lm-ink)" }}>Cookies</h3>
      <p>We use minimal cookies for session management and analytics. Public visitors to your Linkit page get an anonymous, privacy-safe visitor identifier only.</p>
    </LegalShell>
  );
}
export function RefundPage() {
  return (
    <LegalShell title="Refund Policy" updated="February 2026">
      <p>We want you to be happy with Linkit. Here's how refunds work:</p>
      <ul className="ml-5 list-disc space-y-2">
        <li><strong>During the trial:</strong> Nothing to refund — you weren't charged.</li>
        <li><strong>Within 7 days of your first paid charge:</strong> Full refund, no questions. Email hello@linkit.app.</li>
        <li><strong>After 7 days:</strong> We don't offer prorated refunds for the current billing period, but you can cancel any time and won't be charged again.</li>
        <li><strong>Annual plans:</strong> Refund windows apply to the day you were first charged for the annual term.</li>
      </ul>
      <p>Refunds are processed to your original payment method within 5–7 business days.</p>
    </LegalShell>
  );
}

// ------------------------------------------------------------------ CONTACT
export function ContactPage() {
  const [b, setB] = useState({});
  useEffect(() => { api.get("/linkit/branding").then((r) => setB(r.data)).catch(() => setB({})); }, []);
  const email = b.contact_email || "hello@linkit.app";
  return (
    <MarketingLayout>
      <SectionShell size="lg">
        <div className="max-w-3xl">
          <Pill className="mb-6">Contact</Pill>
          <EditorialHeading as="h1" size="xxl">Get in touch.</EditorialHeading>
          <Lede className="mt-6">
            Questions about pricing, features, or anything else? Email us — we reply within one business day.
          </Lede>
          <div className="mt-8">
            <a href={`mailto:${email}`} className="inline-flex items-center gap-2 rounded-full px-6 py-4 text-[16px] font-semibold text-white" style={{ background: "var(--lm-ink)" }} data-testid="contact-email">
              {email}
            </a>
          </div>
        </div>
      </SectionShell>
      <SectionShell size="md" className="bg-[color:var(--lm-cream-2)]/60">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--lm-ink-4)" }}>Support</p>
            <p className="mt-2 text-[15px]" style={{ color: "var(--lm-ink-2)" }}>Bug reports, account issues, billing.</p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--lm-ink-4)" }}>Partnerships</p>
            <p className="mt-2 text-[15px]" style={{ color: "var(--lm-ink-2)" }}>Creator collabs, affiliate programme.</p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--lm-ink-4)" }}>Press</p>
            <p className="mt-2 text-[15px]" style={{ color: "var(--lm-ink-2)" }}>Media enquiries and interviews.</p>
          </div>
        </div>
      </SectionShell>
    </MarketingLayout>
  );
}
